import type { LegalEntityType } from '@festina/legalentity-general-types/legalentity'
import {
  type ExcludeString,
  type Optional,
  ReactUtils,
  type SetRequired,
} from '@festina-life/core'
import { NavLink } from '@festina-life/ui/actions'
import { Tooltip } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import cx from 'classnames'
import { Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { useOptionalIdMapperContext } from '../../context/idMapperContext'
import { useIsAllowedToViewEntity } from '../../hooks/annotation/useIsAllowedToViewEntity'
import { useHook, useService } from '../../lenteHooks'
import { generalRoutes } from '../../routing/generalRoutes'
import { LifeErrorFallback } from '../errorFallback/LifeErrorFallback'

type Props = {
  /**
   * LegalEntity identifier of the respective `LegalEntityType` passed via `kind`.
   */
  id: Optional<string>
  /**
   * LegalEntity entity type (currently either `"person"` or `"company"`).
   */
  kind: ExcludeString<LegalEntityType>
  /**
   * Format in which to render the resolved identifier.
   *
   * `"name"` will format the entity's full name, whereas `"externalId"` will format the person's SSN.
   *
   * @defaultValue `"name"`
   */
  format?: 'name' | 'externalId'
  /**
   * Whether to link to the entity's home page.
   *
   * `"ifExists"` may incur an additional fetch, but can be used
   * when a 404 Not Found may be expected but not desirable.
   *
   * @defaultValue `"always"`
   */
  link?: 'always' | 'ifExists' | 'never'
  /**
   * Fallback to use when fetching fails, while loading,
   * or if the entity does not have a name or came back empty.
   *
   * @defaultValue `"-"`
   */
  fallbackString?: string
}

/**
 * Resolves an internal ID using LegalEntity, displaying
 * either its name or external ID and linking to it by default,
 *
 * The kind will determine how the internal ID is resolved,
 * and how the selected format is applied.
 *
 * The link may be removed if needed. This is useful if
 * this component is wrapped by another link already.
 * The link may optionally be only present if the entity exists.
 * See apgBrpHandlingPartners.md and BrpHandlingPartners.md inside festina-life/components.
 *
 * The format may be changed to show different information.
 * When the format includes the external ID, it will be wrapped
 * inside an element with the `sensitive-info` class so that it can be hidden.
 *
 * A fallback string can be provided for use when the entity has no name.
 *
 * If the `IdMapperProvider` has been mounted above this component,
 * this component will skip making requests.
 * If multiple IDs are expected to be shown all at once, such as in tables,
 * using the provider is strongly recommended.
 * */
function IdToLegalEntityInternal({
  id: internalId,
  kind,
  fallbackString,
  format = 'name',
  link = 'always',
}: SetRequired<Props, 'fallbackString'>) {
  const translate = useTranslate()
  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { getIdentification, getCompanyEntity, getPersonEntity } =
    useOptionalIdMapperContext()

  const isUserAllowedToDoFetch = useIsAllowedToViewEntity({
    overwriteInternalId: internalId ?? undefined,
    businessType: kind,
  })

  const { data: companyVersion } = useHook(
    'fetchers.crm.useFetchCompanyVersionLatest',
  )(
    internalId
      ? {
          tenantId,
          companyId: internalId,
        }
      : null,
    {
      enabled:
        isUserAllowedToDoFetch &&
        kind === 'company' &&
        (format === 'name' || link === 'ifExists') &&
        !getCompanyEntity,
    },
  )
  const companyEntity =
    internalId && kind === 'company'
      ? (getCompanyEntity?.(internalId) ?? companyVersion)
      : null

  const formatFullName = useService('formatter.personEntityNameFormatter')
  const { data: fetchedPersonEntity } = useHook(
    'fetchers.crm.useFetchPersonVersionLatest',
  )(
    internalId
      ? {
          tenantId,
          personId: internalId,
        }
      : null,
    {
      enabled:
        isUserAllowedToDoFetch &&
        kind === 'person' &&
        (format === 'name' || link === 'ifExists') &&
        !getPersonEntity,
    },
  )
  const personEntityName =
    internalId && kind === 'person'
      ? (getPersonEntity?.(internalId).name ??
        fetchedPersonEntity?.personEntityNameVersion)
      : null

  const routeLink = useHook('routing.search.useEntityLookup')(
    kind === 'company' ? '' : (internalId ?? ''),
  )
  const socialSecurityNumberFormatter = useService(
    'formatter.socialSecurityNumberFormatter',
  )
  const { data: fetchedExternalId } = useHook(
    'fetchers.crm.useFetchExternalId',
  )(
    {
      tenantId,
      internalId,
    },
    {
      enabled:
        isUserAllowedToDoFetch && format === 'externalId' && !getIdentification,
    },
  )

  const externalId = internalId
    ? (getIdentification?.(internalId).externalId ?? fetchedExternalId)
    : null

  return isUserAllowedToDoFetch ? (
    <ReactUtils.ConditionalWrap
      condition={
        !!internalId &&
        (link === 'always' ||
          (link === 'ifExists' && (!!companyEntity || !!personEntityName)))
      }
      wrap={(children) => (
        <NavLink
          to={
            kind === 'company'
              ? generalRoutes.company.home({ tenantId, companyId: internalId! })
              : routeLink
          }
        >
          {children}
        </NavLink>
      )}
    >
      <>
        {format === 'name' &&
          (kind === 'person'
            ? formatFullName(personEntityName, fallbackString)
            : (companyEntity?.name ?? fallbackString))}
        {format === 'externalId' && ( // @BETA: this should be a data attribute and also we should consider where to put is as the styling is only loaded in APG currently
          <span className="sensitive-info">
            {(kind === 'person'
              ? socialSecurityNumberFormatter(externalId)
              : externalId) ?? fallbackString}
          </span>
        )}
      </>
    </ReactUtils.ConditionalWrap>
  ) : (
    <Tooltip tip={translate('idToLegalEntity.accessDenied')}>
      <span
        className={cx('cursor-not-allowed opacity-50', {
          'text-primary': link === 'always' || link === 'ifExists',
        })}
        aria-disabled="true"
      >
        {internalId ?? fallbackString}
      </span>
    </Tooltip>
  )
}

export function IdToLegalEntity({ fallbackString = '-', ...props }: Props) {
  return (
    <ErrorBoundary FallbackComponent={LifeErrorFallback}>
      <Suspense fallback={fallbackString}>
        <IdToLegalEntityInternal fallbackString={fallbackString} {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}
